import React, { useState, useEffect } from 'react';
import { salesService } from '../../services/salesService';
import { companyService } from '../../services/api'; // Added companyService
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const InvoiceManagement = () => {
    const [invoices, setInvoices] = useState([]);
    const [dcs, setDcs] = useState([]);
    const [companyProfile, setCompanyProfile] = useState(null); // Added state
    const [loading, setLoading] = useState(true);

    // UI State
    const [showModal, setShowModal] = useState(false);
    const [showTaxModal, setShowTaxModal] = useState(false);
    const [selectedInvoiceForTax, setSelectedInvoiceForTax] = useState(null);

    // Create Invoice Form Data
    const [selectedDcIds, setSelectedDcIds] = useState([]);
    const [dcSelectorSearchTerm, setDcSelectorSearchTerm] = useState(''); // Search state for modal
    const [searchTerm, setSearchTerm] = useState(''); // Search state for main list
    const [invFormData, setInvFormData] = useState({
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: ''
    });

    // Tax Invoice Form Data
    const [taxFormData, setTaxFormData] = useState({
        taxRate: 18,
        buyerNTN: '',
        sellerNTN: ''
    });

    // Edit Invoice State
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({ id: null, date: '', dueDate: '' });

    // Status State
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusData, setStatusData] = useState({ id: null, status: '' });

    useEffect(() => {
        fetchInvoices();
        fetchDcs();
        fetchCompanyProfile();
    }, []);

    const fetchCompanyProfile = async () => {
        try {
            const res = await companyService.getProfile();
            setCompanyProfile(res.data);
        } catch (err) {
            console.error("Failed to fetch company profile", err);
        }
    };

    const fetchInvoices = async () => {
        try {
            const res = await salesService.getInvoices();
            setInvoices(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const fetchDcs = async () => {
        try {
            const res = await salesService.getDeliveryChallans();
            setDcs(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDcSelection = (dcId) => {
        if (selectedDcIds.includes(dcId)) {
            setSelectedDcIds(selectedDcIds.filter(id => id !== dcId));
        } else {
            setSelectedDcIds([...selectedDcIds, dcId]);
        }
    };

    const handleCreateInvoice = async (e) => {
        e.preventDefault();
        if (selectedDcIds.length === 0) return alert('Select at least one Delivery Challan');

        try {
            await salesService.createInvoice({
                dcIds: selectedDcIds,
                invoiceDate: invFormData.invoiceDate,
                dueDate: invFormData.dueDate
            });
            setShowModal(false);
            Swal.fire('Success', 'Invoice Created!', 'success');
            setSelectedDcIds([]);
            fetchInvoices();
        } catch (err) {
            console.error(err);
            setShowModal(false); // Close on error
            Swal.fire('Error', err.response?.data?.error || err.message, 'error');
        }
    };

    const handleCreateTaxInvoice = async (e) => {
        e.preventDefault();
        if (!selectedInvoiceForTax) return;

        try {
            await salesService.createSalesTaxInvoice({
                invoiceId: selectedInvoiceForTax.id,
                ...taxFormData
            });
            setShowTaxModal(false);
            Swal.fire('Success', 'Sales Tax Invoice Generated!', 'success');
            fetchInvoices();
        } catch (err) {
            console.error(err);
            setShowTaxModal(false); // Close on error
            Swal.fire('Error', err.response?.data?.error || err.message, 'error');
        }
    };

    const handleDeleteTaxInvoice = async (invoice) => {
        if (!invoice.SalesTaxInvoice) return;
        const result = await Swal.fire({
            title: 'Delete Sales Tax Invoice?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await salesService.deleteSalesTaxInvoice(invoice.SalesTaxInvoice.id);
                Swal.fire('Deleted!', 'Sales Tax Invoice has been deleted.', 'success');
                fetchInvoices();
            } catch (error) {
                console.error(error);
                Swal.fire('Error', 'Failed to delete.', 'error');
            }
        }
    };

    const openTaxModal = (invoice) => {
        setSelectedInvoiceForTax(invoice);
        setTaxFormData({
            taxRate: 18,
            buyerNTN: invoice.Organization?.ntn || '',
            sellerNTN: companyProfile?.ntn || ''
        });
        setShowTaxModal(true);
    };

    // --- Number to Words Helper (International System) ---
    const numberToWords = (number) => {
        const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const scales = ['', 'Thousand', 'Million', 'Billion', 'Trillion'];

        if (number === 0) return 'Zero';

        // Helper to Convert chunks of 3 digits
        const convertChunk = (num) => {
            let chunkStr = '';
            if (num >= 100) {
                chunkStr += units[Math.floor(num / 100)] + ' Hundred ';
                num %= 100;
            }
            if (num >= 20) {
                chunkStr += tens[Math.floor(num / 10)] + ' ';
                num %= 10;
            }
            if (num > 0) {
                chunkStr += units[num] + ' ';
            }
            return chunkStr;
        }

        let words = '';
        let scaleIndex = 0;

        // Process in chunks of 1000 (3 digits)
        while (number > 0) {
            let chunk = number % 1000;
            if (chunk > 0) {
                let chunkWords = convertChunk(chunk);
                words = chunkWords + scales[scaleIndex] + ' ' + words;
            }
            number = Math.floor(number / 1000);
            scaleIndex++;
        }

        return words.trim();
    };

    // --- PDF Generation ---
    const generateInvoicePDF = async (invoice) => {
        try {
            const companyRes = await companyService.getProfile();
            const company = companyRes.data || {};

            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.width;
            let yPos = 15;

            // --- Header Section (Logos & Company Info) ---
            // Logo (Left aligned)
            if (company.logo && company.logo.startsWith('data:image')) {
                try {
                    const format = company.logo.includes('image/jpeg') ? 'JPEG' : 'PNG';
                    doc.addImage(company.logo, format, 15, yPos, 40, 20);
                } catch (err) { }
            }

            // Distributor Logos (Right aligned)
            let activeLogos = [];
            try {
                // Find associated Quotation to get preference
                const dcLink = (invoice.DeliveryChallans && invoice.DeliveryChallans.length > 0) ? invoice.DeliveryChallans[0] : null;
                const quoteRef = dcLink ? dcLink.Quotation : invoice.Quotation;
                const pref = quoteRef?.distributorLogo || 'default';
                let potentialLogos = [];

                if (company.distributorLogos) {
                    const parsed = JSON.parse(company.distributorLogos);
                    if (Array.isArray(parsed)) potentialLogos = parsed;
                }
                // Fallback legacy
                if (potentialLogos.length === 0 && company.distributorLogo) {
                    potentialLogos.push({ src: company.distributorLogo, active: true });
                }

                if (pref === 'none') {
                    activeLogos = [];
                } else if (pref === 'default' || !pref) {
                    activeLogos = potentialLogos.filter(l => l.active);
                } else if (pref.startsWith('logo_')) {
                    const index = parseInt(pref.replace('logo_', ''));
                    if (potentialLogos[index]) activeLogos = [potentialLogos[index]];
                }
            } catch (e) {
                console.error("Error parsing distributorLogos", e);
            }

            // Render Logos
            if (activeLogos.length > 0) {
                const logoWidth = 35;
                const logoHeight = 15;
                const spacing = 2;
                const rightMargin = pageWidth - 15;

                activeLogos.reverse().forEach((logo, index) => {
                    if (logo.src && logo.src.startsWith('data:image')) {
                        try {
                            const format = logo.src.includes('image/jpeg') ? 'JPEG' : 'PNG';
                            const xPos = rightMargin - ((index + 1) * logoWidth) - (index * spacing);
                            doc.addImage(logo.src, format, xPos, yPos, logoWidth, logoHeight);
                        } catch (err) { console.error("Logo render error", err); }
                    }
                });
            }

            // Company Details (Centered)
            doc.setFontSize(10);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');

            let addrY = yPos + 5;

            // Company Name (Larger & Bold)
            doc.setFontSize(18);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(44, 62, 80); // Dark Blue/Grey
            doc.text(company.name || 'Company Name', pageWidth / 2, addrY, { align: 'center' });

            addrY += 8;

            // Address Details (Normal)
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100); // Dark Grey

            const contactLine = [
                company.phone ? `Tel: ${company.phone}` : '',
                company.email ? `E-mail: ${company.email}` : '',
                company.website ? `${company.website}` : ''
            ].filter(Boolean).join(' | ');

            const addressText = company.address || '';

            doc.text(addressText, pageWidth / 2, addrY, { align: 'center' });
            addrY += 5;
            doc.text(contactLine, pageWidth / 2, addrY, { align: 'center' });
            addrY += 5;

            yPos = addrY + 10;

            // --- Title ---
            doc.setFontSize(22);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0, 0, 0); // Black
            doc.text('INVOICE', pageWidth / 2, yPos, { align: 'center' });
            yPos += 10;

            // --- Client & Info Tables (Side by Side) ---
            // Client Details (Left)
            // Prioritize DC data check
            const dc = (invoice.DeliveryChallans && invoice.DeliveryChallans.length > 0) ? invoice.DeliveryChallans[0] : null;

            // Attention Logic
            let attention = invoice.Organization?.contactPerson || '-';
            if (dc) {
                attention = dc.Quotation?.toContact || dc.receiverName || attention;
            }

            // Location Logic
            let location = invoice.Organization?.location || invoice.Organization?.address || '-';
            if (dc) {
                let displayedLocation = dc.location;
                if (!displayedLocation || displayedLocation === '-') {
                    displayedLocation = dc.Quotation?.Organization?.address || dc.Quotation?.Organization?.location;
                    if (!displayedLocation) {
                        displayedLocation = dc.Organization?.address || dc.Organization?.location;
                    }
                }
                if (displayedLocation) location = displayedLocation;
            }
            // Organization Name
            let orgName = invoice.Organization?.name || '-';
            if (dc && dc.Quotation?.Organization?.name) orgName = dc.Quotation.Organization.name;


            const clientData = [
                ['Attention:', attention],
                ['Organization:', orgName],
                ['Delivery Location:', location]
            ];

            autoTable(doc, {
                startY: yPos,
                head: [[{ content: 'Client Details:', colSpan: 2, styles: { halign: 'center' } }]],
                body: clientData,
                theme: 'plain',
                tableWidth: 90,
                margin: { left: 15 },
                headStyles: {
                    fillColor: [200, 200, 200],
                    textColor: 0,
                    fontStyle: 'bold',
                    halign: 'center',
                    lineWidth: 0.1,
                    lineColor: 0
                },
                bodyStyles: {
                    lineWidth: 0.1,
                    lineColor: 0,
                    cellPadding: 1,
                    fontSize: 9
                },
                columnStyles: {
                    0: { cellWidth: 25, fontStyle: 'bold' },
                    1: { cellWidth: 65 }
                }
            });

            // Invoice Info (Right)
            const dcNos = invoice.DeliveryChallans ? invoice.DeliveryChallans.map(d => d.dcNumber).join(', ') : '-';
            const invInfoData = [
                ['DATE', 'DC #'],
                [invoice.date, dcNos],
                ['INVOICE NO.', 'VENDOR NO.'],
                [invoice.invoiceNumber, company.vendorNumber || '-']
            ];

            autoTable(doc, {
                startY: yPos,
                body: invInfoData,
                theme: 'plain',
                tableWidth: 80,
                margin: { left: pageWidth - 95 },
                styles: {
                    fontSize: 9,
                    cellPadding: 1,
                    lineColor: 0,
                    lineWidth: 0.1,
                    halign: 'center'
                },
                didParseCell: (data) => {
                    if (data.row.index % 2 === 0) {
                        data.cell.styles.fillColor = [200, 200, 200];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            });

            yPos = doc.lastAutoTable.finalY + 10;

            // --- Items Table ---
            const itemRows = [];
            const items = invoice.InvoiceItems || [];

            items.forEach((item, i) => {
                const model = item.QuotationItem?.modelNumber || '-';
                itemRows.push([
                    i + 1,
                    model,
                    item.description,
                    Number(item.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                    item.quantity,
                    Number(item.totalPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                ]);
            });

            const itemColStyles = {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 35, halign: 'center' },
                2: { halign: 'left' },
                3: { cellWidth: 25, halign: 'right' },
                4: { cellWidth: 15, halign: 'center' },
                5: { cellWidth: 25, halign: 'right' }
            };

            autoTable(doc, {
                startY: yPos,
                head: [['Sr. No.', 'MODEL', 'DESCRIPTION', 'UNIT\nPRICE', 'Qty.', 'TOTAL PRICE']],
                body: itemRows,
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    lineColor: 0,
                    lineWidth: 0.1,
                    cellPadding: 2,
                    valign: 'middle',
                    textColor: 0
                },
                headStyles: {
                    fillColor: [44, 62, 80],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    lineColor: 0,
                    lineWidth: 0.1
                },
                columnStyles: itemColStyles
            });

            // --- Footer Table (Attached) ---
            // We create a seamless follow-up table
            const totalWithoutTax = Number(invoice.subTotal);
            const discount = Number(invoice.discount);

            // Fetch Charges from Linked Quotation via DC
            const quote = dc ? dc.Quotation : null;
            const installationCharges = Number(quote?.installationCharges || 0);

            // Calculate GST on Items (Default 18% or from Quote)
            const gstRate = Number(quote?.gstRate || 18);
            const gstAmount = (totalWithoutTax * gstRate) / 100;

            // Calculate Service Tax on Installation (Assuming 16% based on visual analysis: 8000 is 16% of 50000)
            const serviceTaxRate = 16;
            const serviceTaxAmount = (installationCharges * serviceTaxRate) / 100;

            // Grand Total Without Tax (Items + Inst - Disc)
            const grandTotalNoTax = totalWithoutTax + installationCharges - discount;

            // Grand Total (Final)
            const finalGrandTotal = grandTotalNoTax + gstAmount + serviceTaxAmount;

            const amountInWords = numberToWords(Math.round(finalGrandTotal));
            // Capitalize First Words? The helper now returns Title Case ("Three Million...")
            // Just double check we don't double capitalize unnecessarily or miss 'Only'.

            // Rows for Totals
            // Designed to match the screenshot logic derived

            const footerRows = [
                [{ content: 'Total Without Tax', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } }, { content: totalWithoutTax.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), styles: { halign: 'right', fontStyle: 'bold' } }],
                [{ content: 'Installation & Commissioning Charges', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } }, { content: installationCharges.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), styles: { halign: 'right', fontStyle: 'bold' } }],
                [{ content: 'Grand Total Without tax', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } }, { content: grandTotalNoTax.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), styles: { halign: 'right', fontStyle: 'bold' } }],
                [{ content: `GST ${gstRate}%`, colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } }, { content: gstAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), styles: { halign: 'right' } }],
                [{ content: `Service Tax ${serviceTaxRate}%`, colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } }, { content: serviceTaxAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), styles: { halign: 'right' } }],
                [{ content: 'Grand Total I. TAX', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } }, { content: finalGrandTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), styles: { halign: 'right', fontStyle: 'bold' } }],

                // Final Big Row
                [
                    { content: 'Amounts in Words:', colSpan: 2, styles: { valign: 'middle', fontStyle: 'bold', minCellHeight: 20 } },
                    { content: `${amountInWords} Only`, colSpan: 2, styles: { valign: 'middle', fontStyle: 'italic', minCellHeight: 20 } },
                    { content: 'Total', colSpan: 1, styles: { valign: 'middle', halign: 'center', fontStyle: 'bold', minCellHeight: 20 } },
                    { content: finalGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }), colSpan: 1, styles: { valign: 'middle', halign: 'right', fontStyle: 'bold', minCellHeight: 20 } }
                ]
            ];

            autoTable(doc, {
                startY: doc.lastAutoTable.finalY,
                body: footerRows,
                theme: 'grid',
                styles: {
                    fontSize: 9,
                    lineColor: 0,
                    lineWidth: 0.1,
                    cellPadding: 2,
                    textColor: 0
                },
                columnStyles: itemColStyles // Reuse column widths so it aligns perfectly!
            });

            // Footer Signatures
            const sigY = doc.lastAutoTable.finalY + 30;
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text('Authorized Signature: _______________________', pageWidth - 15, sigY, { align: 'right' }); // Right Aligned

            doc.save(`${invoice.invoiceNumber}.pdf`);

        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'Failed to generate PDF', 'error');
        }
    };


    const generateSalesTaxInvoicePDF = async (invoice) => {
        try {
            const sti = invoice.SalesTaxInvoice;
            if (!sti) return alert('No Sales Tax Invoice found');

            const companyRes = await companyService.getProfile();
            const company = companyRes.data || {};

            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.width;
            let yPos = 15;

            // --- Header Section (Consistent with Invoice PDF) ---
            // Logo (Left)
            if (company.logo && company.logo.startsWith('data:image')) {
                try {
                    const format = company.logo.includes('image/jpeg') ? 'JPEG' : 'PNG';
                    doc.addImage(company.logo, format, 15, yPos, 40, 20);
                } catch (err) { }
            }

            // Distributor Logos (Right aligned)
            let activeLogos = [];
            try {
                // Find associated Quotation to get preference
                const dcLink = (invoice.DeliveryChallans && invoice.DeliveryChallans.length > 0) ? invoice.DeliveryChallans[0] : null;
                const quoteRef = dcLink ? dcLink.Quotation : invoice.Quotation;
                const pref = quoteRef?.distributorLogo || 'default';
                let potentialLogos = [];

                if (company.distributorLogos) {
                    const parsed = JSON.parse(company.distributorLogos);
                    if (Array.isArray(parsed)) potentialLogos = parsed;
                }
                // Fallback legacy
                if (potentialLogos.length === 0 && company.distributorLogo) {
                    potentialLogos.push({ src: company.distributorLogo, active: true });
                }

                if (pref === 'none') {
                    activeLogos = [];
                } else if (pref === 'default' || !pref) {
                    activeLogos = potentialLogos.filter(l => l.active);
                } else if (pref.startsWith('logo_')) {
                    const index = parseInt(pref.replace('logo_', ''));
                    if (potentialLogos[index]) activeLogos = [potentialLogos[index]];
                }
            } catch (e) {
                console.error("Error parsing distributorLogos", e);
            }

            // Render Logos
            if (activeLogos.length > 0) {
                const logoWidth = 35;
                const logoHeight = 15;
                const spacing = 2;
                const rightMargin = pageWidth - 15;

                activeLogos.reverse().forEach((logo, index) => {
                    if (logo.src && logo.src.startsWith('data:image')) {
                        try {
                            const format = logo.src.includes('image/jpeg') ? 'JPEG' : 'PNG';
                            const xPos = rightMargin - ((index + 1) * logoWidth) - (index * spacing);
                            doc.addImage(logo.src, format, xPos, yPos, logoWidth, logoHeight);
                        } catch (err) { console.error("Logo render error", err); }
                    }
                });
            }

            // Company Details (Centered)
            doc.setFontSize(10);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');

            let addrY = yPos + 5;

            // Company Name (Larger & Bold)
            doc.setFontSize(18); // Increased size
            doc.setFont(undefined, 'bold');
            doc.setTextColor(44, 62, 80); // Dark Blue/Grey
            doc.text(company.name || 'Company Name', pageWidth / 2, addrY, { align: 'center' });

            addrY += 8;

            // Address Details (Normal)
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(100); // Dark Grey

            const contactLine = [
                company.phone ? `Tel: ${company.phone}` : '',
                company.email ? `E-mail: ${company.email}` : '',
                company.website ? `${company.website}` : ''
            ].filter(Boolean).join(' | ');

            const addressText = company.address || '';

            doc.text(addressText, pageWidth / 2, addrY, { align: 'center' });
            addrY += 5;
            doc.text(contactLine, pageWidth / 2, addrY, { align: 'center' });
            addrY += 5;

            yPos = addrY + 10;

            // --- Title ---
            doc.setFontSize(22);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0, 0, 0); // Black
            doc.text('SALES TAX INVOICE', pageWidth / 2, yPos, { align: 'center' });
            yPos += 15;

            // --- Top Details Section (Split Left/Right) ---
            // Fetch DC and Quote for info
            const dc = (invoice.DeliveryChallans && invoice.DeliveryChallans.length > 0) ? invoice.DeliveryChallans[0] : null;
            const quote = dc ? dc.Quotation : null;

            // Supplier Info (Left) - Text Block approach for control
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text(`Supplier: ${company.name || ''}`, 15, yPos);

            doc.setFont(undefined, 'normal');
            const supplierY = yPos + 5;
            const supplierDetails = [
                `Address: ${company.address || '-'}`,
                `Telephone: ${company.phone || '-'}`,
                `S.T. Registration: ${company.stRegistration || '-'}`,
                `NTN No: ${sti.sellerNTN || company.ntn || '-'}`
            ];

            let supY = supplierY + 5; // Start spacing
            supplierDetails.forEach(line => {
                // Handle multi-line address if needed, but simple for now
                const splitTitle = doc.splitTextToSize(line, 80);
                doc.text(splitTitle, 15, supY);
                supY += (splitTitle.length * 4);
            });


            // Client & Document Info (Right) - Tables
            // Attention Logic
            let attention = invoice.Organization?.contactPerson || '-';
            if (dc) attention = quote?.toContact || dc.receiverName || attention;

            // Location Logic
            let location = invoice.Organization?.location || invoice.Organization?.address || '-';
            if (dc) {
                let displayedLocation = dc.location;
                if (!displayedLocation || displayedLocation === '-') {
                    displayedLocation = quote?.Organization?.address || quote?.Organization?.location;
                    if (!displayedLocation) displayedLocation = dc.Organization?.address || dc.Organization?.location;
                }
                if (displayedLocation) location = displayedLocation;
            }

            const clientDetails = [
                ['Attention:', attention],
                ['Organization:', invoice.Organization?.name || '-'],
                ['Delivery Location:', location],
                ['NTN:', sti.buyerNTN || '-']
            ];

            // Render Client Table
            autoTable(doc, {
                startY: yPos - 5, // Align top
                head: [[{ content: 'Client Details:', colSpan: 2, styles: { halign: 'center', fillColor: [200, 200, 200], textColor: 0, fontStyle: 'bold' } }]],
                body: clientDetails,
                theme: 'plain',
                tableWidth: 80,
                margin: { left: pageWidth - 95 },
                styles: { fontSize: 8, cellPadding: 1, lineColor: 0, lineWidth: 0.1 },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 25 } }
            });

            // Document Info Table (Below Client Table)
            const dcNos = (invoice.DeliveryChallans && invoice.DeliveryChallans.length > 0)
                ? invoice.DeliveryChallans.map(d => d.dcNumber).join(', ')
                : '-';

            const docInfoData = [
                ['DATE', 'DC #'],
                [sti.invoiceDate ? new Date(sti.invoiceDate).toLocaleDateString() : invoice.date, dcNos],
                [{ content: 'STI Number', colSpan: 2, styles: { halign: 'center', fillColor: [200, 200, 200], fontStyle: 'bold' } }],
                [{ content: sti.stInvoiceNumber, colSpan: 2, styles: { halign: 'center' } }]
            ];

            autoTable(doc, {
                startY: doc.lastAutoTable.finalY, // Connect to previous
                body: docInfoData,
                theme: 'plain',
                tableWidth: 80,
                margin: { left: pageWidth - 95 },
                styles: { fontSize: 8, cellPadding: 1, lineColor: 0, lineWidth: 0.1, halign: 'center' },
                didParseCell: (data) => {
                    if (data.row.index === 0) { // Headers
                        data.cell.styles.fillColor = [200, 200, 200];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            });

            // Adjust yPos for Main Table
            yPos = Math.max(supY, doc.lastAutoTable.finalY) + 10;

            // --- Items Table ---
            const itemRows = [];
            let srNo = 1;

            const gstRate = quote?.gstRate || sti.taxRate || 18; // Default 18
            const serviceTaxRate = 16;

            // Invoice Items
            if (invoice.InvoiceItems) {
                invoice.InvoiceItems.forEach(item => {
                    const unitPrice = Number(item.unitPrice);
                    const qty = Number(item.quantity);
                    const totalExcl = Number(item.totalPrice); // Should include discount? Usually STI on Net
                    // Assuming totalExcl is pre-calculated correctly as unit*qty

                    const taxAmt = (totalExcl * gstRate) / 100;
                    const valIncl = totalExcl + taxAmt;

                    itemRows.push([
                        srNo++,
                        item.description,
                        unitPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                        qty,
                        totalExcl.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                        `${gstRate}%`,
                        taxAmt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                        valIncl.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                    ]);
                });
            }

            // Installation Row
            const installCharges = Number(quote?.installationCharges || 0);
            if (installCharges > 0) {
                const taxAmt = (installCharges * serviceTaxRate) / 100;
                const valIncl = installCharges + taxAmt;

                itemRows.push([
                    srNo++,
                    'Installation & Commissioning',
                    installCharges.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), // Unit Price
                    1, // Qty
                    installCharges.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), // Total
                    `${serviceTaxRate}%`,
                    taxAmt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                    valIncl.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                ]);
            }

            // Calculate Grand Totals for Footer
            // Note: We sum the ValIncl column or Recalculate?
            // Safer to re-sum
            let sumTotalExcl = 0;
            let sumTax = 0;
            let sumGrand = 0;

            // Recalculate loops to be safe
            if (invoice.InvoiceItems) {
                invoice.InvoiceItems.forEach(item => {
                    const t = Number(item.totalPrice);
                    sumTotalExcl += t;
                    sumTax += (t * gstRate) / 100;
                });
            }
            if (installCharges > 0) {
                sumTotalExcl += installCharges;
                sumTax += (installCharges * serviceTaxRate) / 100;
            }
            sumGrand = sumTotalExcl + sumTax;


            // Footer Row (Grand Total)
            itemRows.push([
                { content: 'Grand Total', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 0] } }, // Yellow footer as per screenshot?
                { content: sumGrand.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }), styles: { fontStyle: 'bold', fillColor: [255, 255, 0] } }
                // Use Yellow [255, 255, 0] to match screenshot if user insists on layout, but "consistent colors" might imply otherwise.
                // I will use [255, 255, 0] because it's a specific "Grand Total" highlight in the request's referenced image.
            ]);

            // Actually, wait. "consistent colors qwhich you use in invoice pdf".
            // The Invoice PDF has NO yellow. It has clear tables.
            // But the screenshot provided HAS yellow.
            // I will use the Yellow. It's too specific to ignore.

            autoTable(doc, {
                startY: yPos,
                head: [['Sr. No.', 'DESCRIPTION', 'Unit Price\nExcl. of GST', 'Qty.', 'Total Price\nExcl. of GST', 'Rate of\nSales Tax', 'Sales Tax Amount', 'Value Including Sale Tax']],
                body: itemRows,
                theme: 'grid',
                styles: {
                    fontSize: 8,
                    lineColor: 0,
                    lineWidth: 0.1,
                    cellPadding: 2,
                    valign: 'middle',
                    textColor: 0, // Black text
                    halign: 'right' // Default right for numbers
                },
                columnStyles: {
                    0: { halign: 'center', cellWidth: 10 }, // Sr
                    1: { halign: 'left' }, // Desc
                    3: { halign: 'center' }, // Qty
                    5: { halign: 'center' } // Rate
                },
                headStyles: {
                    fillColor: [44, 62, 80],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    lineColor: 0,
                    lineWidth: 0.1
                }
            });

            // Signature
            const sigY = doc.lastAutoTable.finalY + 30;
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text('Authorized Signature: _______________________', pageWidth - 15, sigY, { align: 'right' });

            doc.setFontSize(9);
            doc.text(`For ${company.name || 'Company'}`, pageWidth - 15, sigY + 5, { align: 'right' });

            doc.save(`${sti.stInvoiceNumber}.pdf`);

        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'Failed to generate PDF', 'error');
        }
    };

    // --- Actions Handlers ---

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await salesService.deleteInvoice(id);
                    Swal.fire('Deleted!', 'Invoice has been deleted.', 'success');
                    fetchInvoices();
                } catch (err) {
                    Swal.fire('Error', err.response?.data?.error || err.message, 'error');
                }
            }
        });
    };

    const openEditModal = (inv) => {
        setEditFormData({
            id: inv.id,
            date: inv.date,
            dueDate: inv.dueDate || ''
        });
        setShowEditModal(true);
    };

    const handleUpdateInvoice = async (e) => {
        e.preventDefault();
        try {
            await salesService.updateInvoice(editFormData.id, {
                date: editFormData.date,
                dueDate: editFormData.dueDate
            });
            setShowEditModal(false);
            Swal.fire('Success', 'Invoice updated successfully', 'success');
            fetchInvoices();
            fetchInvoices();
        } catch (err) {
            setShowEditModal(false); // Close on error
            Swal.fire('Error', err.response?.data?.error || err.message, 'error');
        }
    };

    const openStatusModal = (inv) => {
        setStatusData({ id: inv.id, status: inv.status });
        setShowStatusModal(true);
    };

    const handleUpdateStatus = async (status) => {
        try {
            await salesService.updateInvoiceStatus(statusData.id, status);
            setShowStatusModal(false);
            Swal.fire('Success', `Status updated to ${status}`, 'success');
            fetchInvoices();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || err.message, 'error');
        }
    };

    const filteredInvoices = invoices.filter(inv =>
        !searchTerm ||
        (inv.invoiceNumber && inv.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (inv.Organization && inv.Organization.name && inv.Organization.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (inv.date && inv.date.includes(searchTerm)) ||
        (inv.status && inv.status.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h2 className="mb-1" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: '700',
                        fontSize: '2rem'
                    }}>
                        Invoices
                    </h2>
                    <p className="text-muted mb-0">Generate and manage sales invoices</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                    <i className="bi bi-plus-lg me-1"></i> Create Invoice
                </button>
            </div>

            <div className="mb-3">
                <div className="input-group">
                    <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search Invoice by Number, Organization, Date or Status..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Content */}
            <div className="card shadow-sm">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Inv #</th>
                                    <th>Date</th>
                                    <th>Organization</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                    <th>Sales Tax Inv</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="7" className="text-center">Loading...</td></tr>
                                ) : invoices.length === 0 ? (
                                    <tr><td colSpan="7" className="text-center">No Invoices found</td></tr>
                                ) : (
                                    filteredInvoices.map(inv => (
                                        <tr key={inv.id}>
                                            <td><strong>{inv.invoiceNumber}</strong></td>
                                            <td>{inv.date}</td>
                                            <td>{inv.Organization?.name}</td>
                                            <td><strong>Rs. {parseFloat(inv.subTotal).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</strong></td>
                                            <td>
                                                <span className={`badge bg-${inv.status === 'Paid' ? 'success' : 'danger'}`}>
                                                    {inv.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="btn-group">
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => openStatusModal(inv)}
                                                        title="Update Status"
                                                    >
                                                        <i className="bi bi-arrow-repeat"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => openEditModal(inv)}
                                                        title="Edit Invoice"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDelete(inv.id)}
                                                        title="Delete Invoice"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-dark"
                                                        onClick={() => generateInvoicePDF(inv)}
                                                        title="Print Invoice"
                                                    >
                                                        <i className="bi bi-printer"></i>
                                                    </button>
                                                </div>
                                            </td>
                                            <td>
                                                {inv.SalesTaxInvoice ? (
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => generateSalesTaxInvoicePDF(inv)}
                                                            title="Print Tax Invoice"
                                                        >
                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteTaxInvoice(inv)}
                                                            title="Delete Tax Invoice"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="btn btn-sm btn-outline-success"
                                                        onClick={() => openTaxModal(inv)}
                                                        title="Generate Tax Invoice"
                                                    >
                                                        <i className="bi bi-receipt"></i> Generate
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Create Invoice Modal */}
            {showModal && createPortal(
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content" style={{ minHeight: '600px' }}>
                            <div className="modal-header">
                                <h5 className="modal-title">Generate Invoice</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleCreateInvoice}>
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Invoice Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={invFormData.invoiceDate}
                                                onChange={e => setInvFormData({ ...invFormData, invoiceDate: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Due Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={invFormData.dueDate}
                                                onChange={e => setInvFormData({ ...invFormData, dueDate: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Select Delivery Challans (Cumulative)</label>

                                        {/* Search & Add Interface */}
                                        <div className="card border-light bg-light mb-2">
                                            <div className="card-body p-2">
                                                <input
                                                    type="text"
                                                    className="form-control mb-2"
                                                    placeholder="Search by DC #, Project, or Client..."
                                                    value={dcSelectorSearchTerm || ''}
                                                    onChange={(e) => setDcSelectorSearchTerm(e.target.value)}
                                                />
                                                <div className="list-group" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                    {dcs
                                                        .filter(dc =>
                                                            !selectedDcIds.includes(dc.id) && // Hide already selected
                                                            (
                                                                (dc.dcNumber || '').toLowerCase().includes((dcSelectorSearchTerm || '').toLowerCase()) ||
                                                                (dc.Organization?.name || dc.Quotation?.Organization?.name || dc.Quotation?.organization || '').toLowerCase().includes((dcSelectorSearchTerm || '').toLowerCase()) ||
                                                                (dc.Quotation?.Project?.name || '').toLowerCase().includes((dcSelectorSearchTerm || '').toLowerCase())
                                                            )
                                                        )
                                                        .map(dc => {
                                                            const clientName = dc.Organization?.name || dc.Quotation?.Organization?.name || dc.Quotation?.organization || 'Unknown Client';
                                                            return (
                                                                <button
                                                                    key={dc.id}
                                                                    type="button"
                                                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2"
                                                                    onClick={() => handleDcSelection(dc.id)}
                                                                >
                                                                    <div>
                                                                        <strong>{dc.dcNumber}</strong>
                                                                        <span className="text-muted small ms-2">
                                                                            ({clientName}) - {new Date(dc.deliveryDate || dc.date).toLocaleDateString()}
                                                                        </span>
                                                                    </div>
                                                                    <span className="badge bg-primary rounded-pill">+ Add</span>
                                                                </button>
                                                            );
                                                        })}
                                                    {dcs.filter(dc => !selectedDcIds.includes(dc.id)).length === 0 && (
                                                        <div className="text-muted text-center py-2 small">No matching Delivery Challans found</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Selected Items List */}
                                        {selectedDcIds.length > 0 && (
                                            <div className="mt-3">
                                                <h6 className="text-muted small text-uppercase">Selected Items:</h6>
                                                <div className="list-group shadow-sm">
                                                    {selectedDcIds.map(dcId => {
                                                        const dc = dcs.find(d => d.id === dcId);
                                                        if (!dc) return null;
                                                        return (
                                                            <div key={dc.id} className="list-group-item d-flex justify-content-between align-items-center bg-white border-start border-4 border-success py-2">
                                                                <div>
                                                                    <div className="fw-bold">{dc.dcNumber}</div>
                                                                    <div className="small text-muted">{dc.Organization?.name}</div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger border-0"
                                                                    onClick={() => handleDcSelection(dc.id)} // Toggle off
                                                                    title="Remove"
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex justify-content-end">
                                        <button type="button" className="btn btn-secondary me-2" onClick={() => setShowModal(false)}>Cancel</button>
                                        <button type="submit" className="btn btn-primary">Evaluate & Create</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Tax Invoice Modal */}
            {showTaxModal && createPortal(
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Sales Tax Inv</h5>
                                <button type="button" className="btn-close" onClick={() => setShowTaxModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="small text-muted mb-3">For Invoice: <strong>{selectedInvoiceForTax?.invoiceNumber}</strong></p>
                                <form onSubmit={handleCreateTaxInvoice}>
                                    <div className="mb-3">
                                        <label className="form-label">Tax Rate (%)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            value={taxFormData.taxRate}
                                            onChange={e => setTaxFormData({ ...taxFormData, taxRate: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Buyer NTN</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={taxFormData.buyerNTN}
                                            disabled
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Seller NTN</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={taxFormData.sellerNTN}
                                            disabled
                                        />
                                    </div>

                                    <div className="d-flex justify-content-end">
                                        <button type="button" className="btn btn-secondary me-2" onClick={() => setShowTaxModal(false)}>Cancel</button>
                                        <button type="submit" className="btn btn-primary">Generate</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Edit Invoice Modal */}
            {showEditModal && createPortal(
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Invoice</h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleUpdateInvoice}>
                                    <div className="mb-3">
                                        <label className="form-label">Invoice Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={editFormData.date}
                                            onChange={e => setEditFormData({ ...editFormData, date: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Due Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={editFormData.dueDate}
                                            onChange={e => setEditFormData({ ...editFormData, dueDate: e.target.value })}
                                        />
                                    </div>
                                    <div className="d-flex justify-content-end">
                                        <button type="button" className="btn btn-secondary me-2" onClick={() => setShowEditModal(false)}>Cancel</button>
                                        <button type="submit" className="btn btn-primary">Update</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Status Update Modal */}
            {showStatusModal && createPortal(
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Update Status</h5>
                                <button type="button" className="btn-close" onClick={() => setShowStatusModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="d-grid gap-2 mb-3">
                                    <button className="btn btn-outline-warning" onClick={() => handleUpdateStatus('Unpaid')}>Unpaid</button>
                                    <button className="btn btn-outline-success" onClick={() => handleUpdateStatus('Paid')}>Paid</button>
                                    <button className="btn btn-outline-danger" onClick={() => handleUpdateStatus('Cancelled')}>Cancelled</button>
                                </div>
                                <div className="d-flex justify-content-end">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowStatusModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default InvoiceManagement;
